// Mes Trajets — page chauffeur
// Affiche une carte par rapport journalier (RJ-...) avec:
//   • Numéro de rapport + date(s)
//   • Statut (brouillon / en cours / terminé)
//   • Distance parcourue, heures payables
//   • Liste des arrêts (cueillette/livraison/pause) avec heures
//   • Lien vers le rapport complet
import { useState, useEffect, useCallback } from 'react';
import { useApi } from '../hooks/useApi';
import { useNavigate } from 'react-router-dom';
import {
  Navigation,
  Clock,
  MapPin,
  Package,
  Truck,
  RefreshCw,
  ChevronRight,
  Coffee,
  Flag,
  Route as RouteIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatStopDateTime, formatShortDate } from '../lib/dateFormat';
import { formatHoursHuman } from '../lib/hoursFormat';
import ReportGpsMapModal from '../components/ReportGpsMapModal';

const statusBadge = {
  draft:     { label: 'Brouillon', cls: 'bg-slate-700/50 text-slate-300' },
  in_progress: { label: 'En cours', cls: 'bg-amber-500/20 text-amber-300' },
  completed: { label: 'Terminé', cls: 'bg-emerald-500/20 text-emerald-300' },
};

const stopTypeMeta = {
  pickup:     { label: 'Cueillette', icon: Package, color: 'text-blue-400', dot: 'bg-blue-400' },
  delivery:   { label: 'Livraison',  icon: MapPin,  color: 'text-emerald-400', dot: 'bg-emerald-400' },
  pause:      { label: 'Pause',      icon: Coffee,  color: 'text-amber-400', dot: 'bg-amber-400' },
  fin_trajet: { label: 'Fin trajet', icon: Flag,    color: 'text-slate-400', dot: 'bg-slate-400' },
};

const ReportCard = ({ report, onClick, onOpenMap }) => {
  const sb = statusBadge[report.status] || statusBadge.draft;
  const stops = Array.isArray(report.stops) ? report.stops : [];
  const dateLabel = report.end_date && report.end_date !== report.date
    ? `${formatShortDate(report.date)} → ${formatShortDate(report.end_date)}`
    : formatShortDate(report.date);

  return (
    <div
      className="block w-full text-left bg-slate-900 border border-slate-800 rounded-xl overflow-hidden"
      data-testid={`report-card-${report.report_number}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800 bg-slate-950/50">
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onOpenMap(); }}
          className="flex items-center gap-2.5 min-w-0 flex-1 group active:scale-95 transition-transform"
          data-testid={`open-map-${report.report_number}`}
          title="Voir le trajet sur la carte"
        >
          <span className="w-9 h-9 rounded-full bg-amber-500/15 border border-amber-500/40 flex items-center justify-center shrink-0 group-hover:bg-amber-500/25 group-active:bg-amber-500/30">
            <RouteIcon size={18} className="text-amber-400" />
          </span>
          <div className="min-w-0 text-left">
            <h3 className="text-white font-bold text-base truncate">{report.report_number}</h3>
            <p className="text-slate-400 text-xs">{dateLabel}</p>
          </div>
        </button>
        <button
          type="button"
          onClick={onClick}
          className="flex items-center gap-2 shrink-0 ml-2 active:opacity-70"
          data-testid={`open-report-${report.report_number}`}
        >
          <span className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-1 rounded-full ${sb.cls}`}>
            {sb.label}
          </span>
          <ChevronRight size={18} className="text-slate-500" />
        </button>
      </div>

      {/* Stats row — clickable to open report */}
      <button
        type="button"
        onClick={onClick}
        className="w-full grid grid-cols-3 px-4 py-3 border-b border-slate-800 text-center active:bg-slate-800/40"
      >
        <div>
          <p className="text-amber-400 font-bold text-lg leading-tight">{stops.length}</p>
          <p className="text-slate-500 text-[10px] uppercase tracking-wider mt-0.5">Arrêts</p>
        </div>
        <div className="border-x border-slate-800">
          <p className="text-amber-400 font-bold text-lg leading-tight">
            {report.total_km != null ? `${report.total_km}` : '—'}
            <span className="text-xs text-slate-500 ml-1">km</span>
          </p>
          <p className="text-slate-500 text-[10px] uppercase tracking-wider mt-0.5">Distance</p>
        </div>
        <div>
          <p className="text-amber-400 font-bold text-lg leading-tight">
            {report.total_hours != null ? formatHoursHuman(report.total_hours) : '—'}
          </p>
          <p className="text-slate-500 text-[10px] uppercase tracking-wider mt-0.5">Durée</p>
        </div>
      </button>

      {/* Stops timeline — clickable to open report */}
      <button
        type="button"
        onClick={onClick}
        className="w-full text-left active:bg-slate-800/40"
      >
        {stops.length > 0 ? (
          <div className="px-4 py-3">
            <ol className="relative ml-1">
              {stops.map((stop, i) => {
                const meta = stopTypeMeta[stop.stop_type] || stopTypeMeta.delivery;
                const Icon = meta.icon;
                const isLast = i === stops.length - 1;
                return (
                  <li key={stop.id || i} className="relative flex gap-3 pb-3 last:pb-0">
                    {!isLast && (
                      <span className="absolute left-[11px] top-6 bottom-0 w-px bg-slate-700" aria-hidden="true" />
                    )}
                    <div className={`w-6 h-6 rounded-full ${meta.dot} bg-opacity-20 flex items-center justify-center shrink-0 z-10`}>
                      <Icon size={12} className={meta.color} />
                    </div>
                    <div className="flex-1 min-w-0 -mt-0.5">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className={`text-[10px] uppercase tracking-wider font-semibold ${meta.color}`}>
                          {meta.label}
                        </span>
                        <p className="text-white text-sm font-medium truncate flex-1">
                          {stop.client_name || stop.address || '—'}
                        </p>
                      </div>
                      {stop.arrival_datetime && (
                        <p className="text-slate-400 text-xs tabular-nums mt-0.5">
                          {formatStopDateTime(stop.arrival_datetime)}
                          {stop.departure_datetime && (
                            <span className="text-slate-500"> → {formatStopDateTime(stop.departure_datetime)}</span>
                          )}
                        </p>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>
        ) : (
          <div className="px-4 py-3 text-slate-500 text-xs italic">
            Aucun arrêt enregistré
          </div>
        )}
      </button>
    </div>
  );
};

export const RouteSheetPage = () => {
  const { get } = useApi();
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('active'); // active = draft+in_progress
  const [mapReport, setMapReport] = useState(null); // { id, report_number } when open

  const fetchReports = useCallback(async () => {
    try {
      const result = await get('/daily-reports');
      const list = Array.isArray(result?.data) ? result.data : (Array.isArray(result) ? result : []);
      // Sort by date descending (most recent first), then start_time desc
      list.sort((a, b) => {
        const dateCmp = (b.date || '').localeCompare(a.date || '');
        if (dateCmp !== 0) return dateCmp;
        return (b.start_time || '').localeCompare(a.start_time || '');
      });
      setReports(list);
    } catch (err) {
      console.error(err);
      toast.error('Impossible de charger les rapports');
    } finally {
      setLoading(false);
    }
  }, [get]);

  useEffect(() => {
    fetchReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter by status
  const visibleReports = reports.filter(r => {
    if (filter === 'active') return r.status !== 'completed';
    return r.status === 'completed';
  });

  const goToReport = (id) => navigate(`/reports/${id}`);

  if (loading) {
    return (
      <div className="page-wrapper p-4" data-testid="route-loading">
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-slate-900 rounded-xl p-4 animate-pulse h-40">
              <div className="h-4 bg-slate-800 rounded w-1/3 mb-3" />
              <div className="h-4 bg-slate-800 rounded w-2/3" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper" data-testid="route-page">
      {/* Header */}
      <div className="p-4 pb-2">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Navigation className="text-amber-500" size={24} />
            <div>
              <h1 className="text-xl font-bold text-white">Mes trajets</h1>
              <p className="text-slate-400 text-xs">
                {new Date().toLocaleDateString('fr-CA', { weekday: 'long', day: 'numeric', month: 'long' })}
              </p>
            </div>
          </div>
          <button
            onClick={() => { setLoading(true); fetchReports(); }}
            className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700"
            data-testid="refresh-reports"
            type="button"
          >
            <RefreshCw size={18} className="text-slate-400" />
          </button>
        </div>

        {/* Filter tabs */}
        <div className="flex gap-2 mb-4">
          {[
            { key: 'active', label: 'En cours / Brouillons' },
            { key: 'completed', label: 'Terminés' },
          ].map(f => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === f.key ? 'bg-amber-500 text-black' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
              }`}
              data-testid={`filter-${f.key}`}
              type="button"
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Reports list */}
      <div className="px-4 pb-6 space-y-3">
        {visibleReports.length === 0 ? (
          <div className="text-center py-12" data-testid="no-reports">
            <Truck className="mx-auto mb-3 text-slate-600" size={48} />
            <h3 className="text-lg font-bold text-white mb-1">Aucun trajet</h3>
            <p className="text-slate-400 text-sm">
              {filter === 'active'
                ? 'Aucun rapport en cours. Crée-en un depuis l\'Accueil.'
                : 'Aucun rapport terminé.'}
            </p>
          </div>
        ) : (
          visibleReports.map(r => (
            <ReportCard
              key={r.id}
              report={r}
              onClick={() => goToReport(r.id)}
              onOpenMap={() => setMapReport({ id: r.id, report_number: r.report_number })}
            />
          ))
        )}
      </div>

      {mapReport && (
        <ReportGpsMapModal
          open={!!mapReport}
          onClose={() => setMapReport(null)}
          reportId={mapReport.id}
          reportNumber={mapReport.report_number}
        />
      )}
    </div>
  );
};

export default RouteSheetPage;
